import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import * as Font from "expo-font";
import _ from "lodash";
import { Platform } from "react-native";
import { createStore } from "src/mobx/createStore";
import { createPersistence } from "src/services/createPersistence";
import { environment } from "./environment";
import { initializeI18n } from "./features/intl/initializeI18n";
import { initializeSentry } from "./initializeSentry";
import { createQueryClient } from "./services/createQueryClient";
// Initialize Firebase for web
// (we use .web.ts extension for them but that will be resolved by the bundler)
import "./features/analytics/firebaseAnalytics";
import "./features/analytics/firebaseApp";
import { createBaseAuthHttp } from "./services/http/createBaseAuthHttp";
import { createBaseHttp } from "./services/http/createBaseHttp";
import { createTosHttp } from "./services/http/createTosHttp";
import { ignoreLogs } from "./utils/ignoreLogs";
import { bookingDataLayerPush } from "./utils/logBookingEvent";
import type { Href } from "expo-router";

export async function initialize() {
  // Make sure this is always at the top of the initialize function
  await initializeSentry();

  if (environment.DISABLE_LOGS) {
    ignoreLogs();
  }

  await initializeI18n();

  dayjs.extend(utc);
  dayjs.extend(relativeTime);
  dayjs.extend(localizedFormat);
  dayjs.extend(timezone);
  dayjs.extend(customParseFormat);

  // Loading fonts in runtime is needed for web only, on native apps fonts are loaded using config plugins in app.json
  if (Platform.OS === "web") {
    try {
      await Font.loadAsync({
        "Inter-ExtraBold": require("../src/assets/fonts/Inter-ExtraBold.otf"),
        "Inter-Regular": require("../src/assets/fonts/Inter-Regular.otf"),
        "Inter-SemiBold": require("../src/assets/fonts/Inter-SemiBold.otf"),
        "theSansB-W3Light": require("../src/assets/fonts/TheSansB-W3Light.otf"),
        "theSansB-W5Plain": require("../src/assets/fonts/TheSansB-W5Plain.otf"),
        "theSansB-W7Bold": require("../src/assets/fonts/TheSansB-W7Bold.otf"),
        "theSansB-W8ExBold": require("../src/assets/fonts/TheSansB-W8ExtraBold.otf"),
        "AlbertSans-Regular": require("../src/assets/fonts/AlbertSans-Regular.ttf"),
        "AlbertSans-SemiBold": require("../src/assets/fonts/AlbertSans-SemiBold.ttf"),
        "AlbertSans-Bold": require("../src/assets/fonts/AlbertSans-Bold.ttf"),
        "AlbertSans-ExtraBold": require("../src/assets/fonts/AlbertSans-ExtraBold.ttf"),
        "DMSans-Regular": require("../src/assets/fonts/DMSans-Regular.ttf"),
        "DMSans-SemiBold": require("../src/assets/fonts/DMSans-SemiBold.ttf"),
        "DMSans-Bold": require("../src/assets/fonts/DMSans-Bold.ttf"),
        "DMSans-ExtraBold": require("../src/assets/fonts/DMSans-ExtraBold.ttf"),
      });
    } catch (error) {
      // Don't crash the app, just log the error
      console.log("Error loading fonts", error);
    }
  }

  const queryClient = createQueryClient();
  const baseAuthHttp = createBaseAuthHttp(() => store);
  const baseHttp = createBaseHttp();

  // We have three different axios instances for different APIs
  const tosHttp = createTosHttp();
  const persistence = createPersistence();

  const store = await createStore({
    tosHttp,
    baseAuthHttp,
    baseHttp,
    persistence,
    queryClient,
  });

  await store.authStore.silentLogin();

  // Add an interceptor to catch 401 errors and log out the
  // We have to define it here because we need access to the store
  baseAuthHttp.interceptors.response.use(_.identity, async (error) => {
    if (error.response?.status === 401) {
      console.log("🔑 UNAUTHORIZED, LOGGING OUT");

      // clinicId assumes that the user is in the standalone booking flow
      const clinicId = new URLSearchParams(window.location.search).get(
        "clinicId",
      );

      const redirectHref: Href = clinicId
        ? `/(standalone)/booking-login?clinicId=${clinicId}`
        : "/(auth)/login";

      if (clinicId) {
        // Log the user out of the booking data layer
        bookingDataLayerPush({
          event: "bankid_identify_logout_web_app",
        });
      }

      store.authStore.logout(redirectHref);
    }

    return Promise.reject(error);
  });

  return {
    store,
    queryClient,
  };
}
